import { useEffect, useState } from 'react'

import {
  useCoordinator,
  useGetSettings,
  UUID,
} from '@builddocs/data-management'
import { useCheckOperation } from '@builddocs/data-management'
import { useDisclosure } from '@builddocs/system'
import { LoadingButton } from '@mui/lab'
import { Button, Collapse, Stack, SxProps, TextField } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { addDays } from 'date-fns'
import { Controller, useForm } from 'react-hook-form'

import HelpBubble from '../../molecules/help-bubble/HelpBubble'

type OwnProps = {
  closingId: UUID
  canInspect: boolean
  sx?: SxProps
  onDeviate?: (
    body: Record<string, any>,
    additionalInfo: Record<string, any>,
    onSuccess: () => any,
    query: Record<string, any>,
  ) => void
  commentEntity: {
    place: string
    docType: string
    docId: UUID
    name: string
  }
  isShowButtonForAll?: boolean
  refreshComments: () => void
}

function AddComment({
  closingId,
  onDeviate,
  refreshComments,
  isShowButtonForAll,
  commentEntity,
  canInspect,
  sx,
}: OwnProps) {
  const { projectId } = useCoordinator()
  const [isLoading, setIsLoading] = useState(false)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { checkOperation } = useCheckOperation()

  const handleOpen = () => {
    checkOperation({
      operationId: 'CREATE_REMARK',
      operationCallback: onOpen,
    })
  }

  const {
    data: setting,
    isLoading: isSettingsLoading,
    isError,
  } = useGetSettings({
    projectId,
  })

  const { handleSubmit, errors, control, reset } = useForm({
    defaultValues: {
      information: commentEntity['place'] ?? '',
      problemDescription: '',
      isAllClosing: false,
      deadline: setting?.remarkingDaysMax
        ? addDays(new Date(), setting?.remarkingDaysMax)
        : null,
    },
  })

  useEffect(() => {
    if (!isError && !isSettingsLoading) {
      reset({
        information: commentEntity['place'] ?? '',
        problemDescription: '',
        isAllClosing: false,
        deadline: setting?.remarkingDaysMax
          ? addDays(new Date(), setting?.remarkingDaysMax)
          : null,
      })
    }
  }, [isSettingsLoading])

  const onCreate = data => {
    if (onDeviate) {
      setIsLoading(true)
      onDeviate(
        {
          information: data.information,
          problemDescription: data.problemDescription,
          deadline: data.deadline,
        },
        {
          externalEntityId: commentEntity.docId,
          externalEntityInfo: data.isAllClosing
            ? 'CLOSING_ALL_DOC'
            : commentEntity.docType,
          workTypeComment: { workType: 'workType' },
          name: commentEntity.name,
        },
        () => {
          setIsLoading(false)
          refreshComments()
          onClose()
        },
        {
          toEveryActForClosingId: data.isAllClosing ? closingId : undefined,
        },
      )
    }
  }

  return (
    <Stack spacing={1}>
      <Stack alignItems="center" direction="row" spacing={1}>
        {!isOpen && (
          <Button disabled={!canInspect} sx={sx} onClick={handleOpen}>
            Добавить замечание
          </Button>
        )}
        {!canInspect && (
          <HelpBubble
            description="Вы не можете оставлять замечания, так как не обладаете правами
            проверяющего в договоре."
            kind="info"
            title="Информация"
            width={320}
          />
        )}
      </Stack>
      <form style={{ position: 'relative' }} onSubmit={handleSubmit(onCreate)}>
        <Collapse unmountOnExit in={isOpen} timeout="auto">
          <Stack spacing={2} sx={{ mb: 2 }}>
            <Stack direction="row" spacing={2}>
              <Controller
                as={
                  <TextField
                    fullWidth
                    multiline
                    disabled={isLoading}
                    error={!!errors?.information?.message}
                    helperText={errors?.information?.message}
                    label="Место в документах"
                    name="information"
                    size="small"
                  />
                }
                control={control}
                name="information"
              />
              <Controller
                as={
                  <TextField
                    fullWidth
                    multiline
                    disabled={isLoading}
                    error={!!errors?.problemDescription?.message}
                    helperText={errors?.problemDescription?.message}
                    label="Комментарий"
                    name="problemDescription"
                    size="small"
                  />
                }
                control={control}
                name="problemDescription"
                rules={{
                  required: 'Должно быть заполнено',
                }}
              />
            </Stack>
            <Stack direction="row">
              <Controller
                control={control}
                name="deadline"
                render={({ onChange, value, ...rest }) => (
                  <DatePicker
                    {...rest}
                    clearable
                    label="Дедлайн"
                    mask="__.__.____"
                    minDate={new Date()}
                    renderInput={params => (
                      <TextField
                        {...params}
                        error={!!errors?.deadline?.message}
                        helperText={errors?.deadline?.message}
                        size="small"
                      />
                    )}
                    value={value}
                    onChange={newValue => {
                      onChange(newValue)
                    }}
                  />
                )}
                rules={{
                  required: setting?.remarkingDaysMax
                    ? 'Должно быть заполнено'
                    : undefined,
                }}
              />
              <TextField sx={{ display: 'none' }} />
            </Stack>
            <Stack direction="row" justifyContent="space-between" spacing={1}>
              {isShowButtonForAll && (
                <></>
                // <Controller
                //   control={control}
                //   name="isAllClosing"
                //   render={({ onChange, onBlur, value, ref, ...rest }) => (
                //     <FormControlLabel
                //       control={
                //         <></>
                //         // <Checkbox
                //         //   checked={value}
                //         //   inputRef={ref}
                //         //   onBlur={onBlur}
                //         //   onChange={e => onChange(e.target.checked)}
                //         //   {...rest}
                //         // />
                //       }
                //       disabled={isLoading}
                //       label="Применить ко всем актам закрытия"
                //       {...rest}
                //     />
                //   )}
                // />
              )}
              <Stack direction="row" spacing={1}>
                <Button disabled={isLoading} onClick={onClose}>
                  Отмена
                </Button>
                <LoadingButton
                  id="act-comments-drawer-button-add-comment"
                  loading={isLoading}
                  type="submit"
                >
                  Сохранить
                </LoadingButton>
              </Stack>
            </Stack>
          </Stack>
        </Collapse>
      </form>
    </Stack>
  )
}

export default AddComment
